import React from 'react';
import { Link } from 'react-router-dom';
import { useOrganization } from '../context/OrganizationContext';
import { Subscription } from '../types';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Business as BusinessIcon,
  MonetizationOn as SubscriptionIcon,
  ArrowForward,
} from '@mui/icons-material';

const getPlanColor = (plan: string) => {
  switch (plan) {
    case 'free':
      return 'default';
    case 'standard':
      return 'primary';
    case 'premium':
      return 'secondary';
    default:
      return 'default';
  }
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString();
};

const Subscriptions: React.FC = () => {
  const { organizations, subscriptions, isLoading } = useOrganization();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  const orgSubscriptions = organizations.map(org => {
    // Find subscription for this org, or create a default one
    const orgSub = Object.values(subscriptions).find(sub => sub.organizationId === org.id) || {
      _id: 'free-default',
      name: 'Free Plan',
      plan: 'free',
      organizationId: org.id,
      status: 'active',
      startDate: new Date().toISOString(),
      endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      teams: [],
      limits: {
        teamsPerOrg: 1,
        usersPerOrg: 1,
        storage: 5
      },
      usage: {
        teamsCount: 0,
        activeUsers: 0,
        storageUsed: 0
      },
      autoRenew: false,
      nextBillingDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    return {
      organization: org,
      subscription: orgSub
    };
  });

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
        Subscription Management
      </Typography>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Your Subscriptions
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Organization</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Expires</TableCell>
                <TableCell>Teams</TableCell>
                <TableCell>Users</TableCell>
                <TableCell>Storage</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orgSubscriptions.map(({ organization, subscription }) => (
                <TableRow key={organization.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <BusinessIcon sx={{ mr: 1, fontSize: '1.2rem', color: 'primary.main' }} />
                      {organization.name}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={subscription.plan.charAt(0).toUpperCase() + subscription.plan.slice(1)}
                      color={getPlanColor(subscription.plan)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={subscription.status.toUpperCase()}
                      color={subscription.status === 'active' || subscription.status === 'trial' ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(subscription.endDate)}</TableCell>
                  <TableCell>
                    {subscription.limits.teamsPerOrg === 9999 ? 'Unlimited' : subscription.limits.teamsPerOrg}
                  </TableCell>
                  <TableCell>{subscription.limits.usersPerOrg}</TableCell>
                  <TableCell>{subscription.limits.storage} GB</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      endIcon={<ArrowForward />}
                      component={Link}
                      to={`/organizations/${organization.id}?tab=3`}
                    >
                      Manage
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Typography variant="h5" gutterBottom>
        Available Plans
      </Typography>
      <Grid container spacing={3}>
        <Grid xs={12} md={4}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Free
              </Typography>
              <Typography variant="h6" color="primary" gutterBottom>
                $0/month
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" paragraph>
                • 1 Organization
              </Typography>
              <Typography variant="body2" paragraph>
                • 1 Team
              </Typography>
              <Typography variant="body2" paragraph>
                • 1 Concurrent User
              </Typography>
              <Typography variant="body2" paragraph>
                • 5 GB Storage
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid xs={12} md={4}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Standard
              </Typography>
              <Typography variant="h6" color="primary" gutterBottom>
                $200/month
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" paragraph>
                • 3 Organizations
              </Typography>
              <Typography variant="body2" paragraph>
                • 3 Teams per Organization
              </Typography>
              <Typography variant="body2" paragraph>
                • 10 Concurrent Users
              </Typography>
              <Typography variant="body2" paragraph>
                • 100 GB Storage
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid xs={12} md={4}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Premium
              </Typography>
              <Typography variant="h6" color="primary" gutterBottom>
                $500/month
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" paragraph>
                • Unlimited Organizations
              </Typography>
              <Typography variant="body2" paragraph>
                • Unlimited Teams
              </Typography>
              <Typography variant="body2" paragraph>
                • 20 Concurrent Users
              </Typography>
              <Typography variant="body2" paragraph>
                • 300 GB Storage
              </Typography>
              <Typography variant="body2" paragraph>
                • Add 10 extra users for $100/mo
              </Typography>
              <Typography variant="body2" paragraph>
                • Add 100GB storage for $25/mo
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Subscriptions;